import { useState, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Calculator, Download, AlertTriangle, Monitor, Users, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

const SoftwareVisitorManagement = ({ onBack }) => {
  const [projectName, setProjectName] = useState("");
  const [monthlyVisitors, setMonthlyVisitors] = useState("");
  const [registrationSites, setRegistrationSites] = useState("");
  const [dedicatedTerminal, setDedicatedTerminal] = useState("");
  const [accessControl, setAccessControl] = useState("");
  
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showContactRep, setShowContactRep] = useState(false);
  const [showAccessControlDialog, setShowAccessControlDialog] = useState(false);
  
  const quoteBoxRef = useRef(null);

  // Get user info for role-based pricing
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const userRole = user?.role || 'installer';
  
  const isAdmin = userRole === 'admin' || userRole === 'zkteco_admin';
  const isDistributor = userRole === 'distributor' || userRole === 'distributor_admin';
  const showDistributorPricing = isAdmin || isDistributor;
  const showInstallerPricing = isAdmin || isDistributor;
  const showEndUserPricing = true;

  const handleCalculate = async () => {
    setError("");
    setShowContactRep(false);
    
    // Validation
    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }
    if (!monthlyVisitors) {
      setError("Please select anticipated monthly visitors");
      return;
    }
    if (!registrationSites) {
      setError("Please select number of registration sites");
      return;
    }
    
    // Check if monthly visitors is 20001+ - show contact rep message
    if (monthlyVisitors === "20001+") {
      setShowContactRep(true);
      setResult(null);
      return;
    }
    
    if (!dedicatedTerminal) {
      setError("Please select if you need a dedicated access control terminal");
      return;
    }
    
    // Only ask about access control if dedicatedTerminal is "yes"
    if (dedicatedTerminal === "yes" && !accessControl) {
      setError("Please select if you require a level of access control");
      return;
    }
    
    // If access control is needed, show contact rep
    if (accessControl === "yes") {
      setShowContactRep(true);
      setResult(null);
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        project_name: projectName,
        monthly_visitors: monthlyVisitors,
        registration_sites: parseInt(registrationSites),
        dedicated_terminal: dedicatedTerminal === "yes",
        access_control_required: accessControl === "yes"
      };
      
      const response = await axios.post(`${API}/configure-visitor-management`, payload, getAuthHeaders());
      setResult(response.data);
    } catch (err) {
      console.error('Error calculating quote:', err);
      setError(err.response?.data?.detail || "Failed to calculate quote");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQuote = async () => {
    if (!quoteBoxRef.current) return;
    
    try {
      const canvas = await html2canvas(quoteBoxRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `${projectName || 'VisitorManagement'}_Quote.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading quote:', err);
      setError('Failed to download quote image');
    }
  };

  const handleNewQuote = () => {
    const hasData = projectName || monthlyVisitors || registrationSites;
    
    if (hasData) {
      const confirmed = window.confirm("Start a new quote?\n\nAny unsaved changes will be lost.");
      if (!confirmed) return;
    }
    
    setProjectName("");
    setMonthlyVisitors("");
    setRegistrationSites("");
    setDedicatedTerminal("");
    setAccessControl("");
    setResult(null);
    setError("");
    setShowContactRep(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm">Back to Visitor Management</span>
          </Button>
        </div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Software Based Visitor Management</h1>
          <p className="text-sm text-slate-500">Configure your cloud-based visitor management solution</p>
        </div>

        {/* Error Messages */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Contact Rep Message */}
        {showContactRep && (
          <Alert className="mb-4 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              <strong>Please speak to your ZKTeco Representative for a full solution build.</strong>
              <br />
              Your requirements exceed our standard configuration options and require a custom solution.
            </AlertDescription>
          </Alert>
        )}

        {/* Project Details */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name <span className="text-red-500">*</span></Label>
              <Input
                id="project-name"
                placeholder="Enter project name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="max-w-md"
              />
            </div>
          </CardContent>
        </Card>

        {/* Monthly Visitors */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Anticipated Monthly Visitors
            </CardTitle>
            <CardDescription>Select the expected number of visitors per month</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={monthlyVisitors} onValueChange={setMonthlyVisitors} className="space-y-2">
              <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                monthlyVisitors === "0-5000" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`} onClick={() => setMonthlyVisitors("0-5000")}>
                <RadioGroupItem value="0-5000" id="visitors-1" />
                <Label htmlFor="visitors-1" className="cursor-pointer flex-1">0 - 5,000 visitors</Label>
              </div>
              <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                monthlyVisitors === "5001-10000" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`} onClick={() => setMonthlyVisitors("5001-10000")}>
                <RadioGroupItem value="5001-10000" id="visitors-2" />
                <Label htmlFor="visitors-2" className="cursor-pointer flex-1">5,001 - 10,000 visitors</Label>
              </div>
              <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                monthlyVisitors === "10001-20000" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`} onClick={() => setMonthlyVisitors("10001-20000")}>
                <RadioGroupItem value="10001-20000" id="visitors-3" />
                <Label htmlFor="visitors-3" className="cursor-pointer flex-1">10,001 - 20,000 visitors</Label>
              </div>
              <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                monthlyVisitors === "20001+" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`} onClick={() => setMonthlyVisitors("20001+")}>
                <RadioGroupItem value="20001+" id="visitors-4" />
                <Label htmlFor="visitors-4" className="cursor-pointer flex-1">20,001+ visitors</Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Registration Sites */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Number of Registration Sites
            </CardTitle>
            <CardDescription>A workstation is considered a registration site</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={registrationSites} onValueChange={setRegistrationSites} className="space-y-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <div key={num} className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  registrationSites === String(num) ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`} onClick={() => setRegistrationSites(String(num))}>
                  <RadioGroupItem value={String(num)} id={`sites-${num}`} />
                  <Label htmlFor={`sites-${num}`} className="cursor-pointer flex-1">
                    {num} Registration Site{num > 1 ? 's' : ''}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Dedicated Terminal Question - Only show if not 20001+ */}
        {monthlyVisitors && monthlyVisitors !== "20001+" && (
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Dedicated Access Control Terminal</CardTitle>
              <CardDescription>Do you want to use a dedicated access control terminal to check in?</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={dedicatedTerminal} onValueChange={setDedicatedTerminal} className="space-y-2">
                <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  dedicatedTerminal === "yes" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`} onClick={() => setDedicatedTerminal("yes")}>
                  <RadioGroupItem value="yes" id="terminal-yes" />
                  <Label htmlFor="terminal-yes" className="cursor-pointer flex-1">Yes</Label>
                </div>
                <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  dedicatedTerminal === "no" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`} onClick={() => setDedicatedTerminal("no")}>
                  <RadioGroupItem value="no" id="terminal-no" />
                  <Label htmlFor="terminal-no" className="cursor-pointer flex-1">No</Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {/* Access Control Question - Only show if dedicated terminal is yes */}
        {dedicatedTerminal === "yes" && (
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Access Control Requirements</CardTitle>
              <CardDescription>Do you require a level of access control, or will visitors be chaperoned around the site?</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={accessControl} onValueChange={(value) => {
                setAccessControl(value);
                if (value === "yes") {
                  setShowAccessControlDialog(true);
                }
              }} className="space-y-2">
                <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  accessControl === "yes" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`} onClick={() => {
                  setAccessControl("yes");
                  setShowAccessControlDialog(true);
                }}>
                  <RadioGroupItem value="yes" id="access-yes" />
                  <Label htmlFor="access-yes" className="cursor-pointer flex-1">Yes, access control is required</Label>
                </div>
                <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  accessControl === "no" ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`} onClick={() => setAccessControl("no")}>
                  <RadioGroupItem value="no" id="access-no" />
                  <Label htmlFor="access-no" className="cursor-pointer flex-1">No, visitors will be chaperoned</Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {/* Access Control Dialog */}
        <Dialog open={showAccessControlDialog} onOpenChange={setShowAccessControlDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Phone className="h-5 w-5" />
                Contact ZKTeco UK
              </DialogTitle>
            </DialogHeader>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-slate-700 leading-relaxed">
                ZKTeco offer a fully integrated solution for visitor management and access control. But due to the many variations of this solution, it is too complex for the configurator to build.
              </p>
              <p className="text-slate-700 mt-4 font-medium">
                Please contact ZKTeco UK on <span className="text-blue-700 font-bold">0121 790 1600</span>
              </p>
              <p className="text-slate-600 mt-4 text-sm italic">
                Thank you for Choosing ZKTeco
              </p>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setShowAccessControlDialog(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Action Buttons */}
        <div className="flex justify-between mb-4">
          <Button variant="outline" onClick={handleNewQuote} data-testid="new-quote-btn">
            <Calculator className="h-4 w-4 mr-2" />
            New Quote
          </Button>
          <Button 
            onClick={handleCalculate}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Calculator className="h-4 w-4 mr-2" />
            {loading ? "Calculating..." : "Calculate and Save"}
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div ref={quoteBoxRef}>
            <Card className="mt-6 border-green-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-green-700">Quote Summary</CardTitle>
                  <CardDescription>Configuration for {result.project_name}</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadQuote}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Quote
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Configuration Summary */}
                <div className="bg-slate-50 p-4 rounded-md">
                  <h4 className="font-medium mb-2">Configuration</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Monthly Visitors: {result.configuration?.monthly_visitors}</li>
                    <li>• Registration Sites: {result.configuration?.registration_sites}</li>
                    <li>• Dedicated Terminal: {result.configuration?.dedicated_terminal ? 'Yes' : 'No'}</li>
                  </ul>
                </div>

                {/* BOM Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2">SKU</th>
                        <th className="text-left p-2">Description</th>
                        <th className="text-center p-2">Qty</th>
                        {showDistributorPricing && <th className="text-right p-2">Dist Price</th>}
                        {showInstallerPricing && <th className="text-right p-2">Inst Price</th>}
                        {showEndUserPricing && <th className="text-right p-2">EU Price</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {result.bom?.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-2 font-mono text-xs">{item.sku}</td>
                          <td className="p-2">{item.description}</td>
                          <td className="text-center p-2">{item.quantity}</td>
                          {showDistributorPricing && <td className="text-right p-2">£{item.distributor_total?.toFixed(2)}</td>}
                          {showInstallerPricing && <td className="text-right p-2">£{item.installer_total?.toFixed(2)}</td>}
                          {showEndUserPricing && <td className="text-right p-2">£{item.enduser_total?.toFixed(2)}</td>}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 font-semibold">
                      <tr>
                        <td colSpan="3" className="p-2 text-right">Totals:</td>
                        {showDistributorPricing && <td className="text-right p-2">£{result.total_distributor?.toFixed(2)}</td>}
                        {showInstallerPricing && <td className="text-right p-2">£{result.total_installer?.toFixed(2)}</td>}
                        {showEndUserPricing && <td className="text-right p-2">£{result.total_enduser?.toFixed(2)}</td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Profit Margin for Distributors/Admins */}
                {showDistributorPricing && result.profit_margin && (
                  <div className="bg-green-50 p-4 rounded-md">
                    <h4 className="font-medium text-green-700 mb-2">Profit Margin</h4>
                    <p className="text-green-600">
                      £{result.profit_margin?.toFixed(2)} ({result.profit_percentage?.toFixed(1)}% margin)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoftwareVisitorManagement;
